import { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import type { Reservation } from '@luxuryescapes/contract-svc-reservation';

import { MAX_SVC_IMAGE_UPLOAD_BYTES, getUploadedImageId, uploadImage } from '~/services/ImageService';

import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

import { type LocalImageEdit, buildDefaultEdit } from './ImagesVideo/ImageEditor/ImageEditorUtils';
import type { MediaImage, PropertyRoomTypeWithImages, RoomOption, RoomSelection } from './ImagesVideo/types';
import {
  DEFAULT_ROOM_NAME,
  DEFAULT_SOURCE_ROOM_NAME,
  UNASSIGNED_IMAGE_LABEL,
  allSettledWithConcurrency,
  buildMediaImages,
  buildUploadPlans,
  dedupeMediaImages,
  getPrimaryVideoId,
  getRoomKey,
  isAllowedImageFile,
  togglePublicId,
} from './ImagesVideo/utils';
import type { UploadedRoomAssignment } from './ImagesVideo/utils';

const IMAGE_UPLOAD_CONCURRENCY = 5;
const UNASSIGNED_ROOM_OPTION: RoomSelection = { roomName: DEFAULT_ROOM_NAME };

interface UseOfferBuilderMediaParams {
  images?: Array<App.OfferImage>;
  propertyImages?: Array<Reservation.Image>;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  videos?: {
    luxuryescapes?: {
      vimeo_id?: string | null;
    };
  };
}

export interface OfferBuilderMediaController {
  deletedImagePublicIds: Array<string>;
  hiddenImagePublicIds: Array<string>;
  imageEdits: Record<string, LocalImageEdit>;
  imagePendingDeletion?: MediaImage;
  isUploadingImages: boolean;
  mediaImages: Array<MediaImage>;
  roomImagePendingEdit?: MediaImage;
  roomOptions: Array<RoomOption>;
  uploadedRoomAssignments: Array<UploadedRoomAssignment>;
  videoId?: string;
  closeDeleteDialog: () => void;
  confirmDeleteImage: () => void;
  deleteRoomImageFromEditor: (image: MediaImage) => void;
  deleteImage: (publicId: string) => void;
  removeImageFromRoom: (publicId: string) => void;
  saveImageEdit: (publicId: string, edit: LocalImageEdit) => void;
  setRoomImagePendingEdit: (image?: MediaImage) => void;
  setVideoId: (videoId?: string) => void;
  toggleHiddenImage: (publicId: string) => void;
  updateImageTitle: (publicId: string, title: string) => void;
  uploadFiles: (files: Array<File>, options?: { ignoreNonImageFiles?: boolean }) => Promise<void>;
  assignImagesToRoom: (roomId: string, roomName: string, selectedImages: Array<MediaImage>) => void;
  editRoomImage: (publicId: string) => void;
}

export default function useOfferBuilderMedia({
  images = [],
  propertyImages,
  propertyRoomTypes,
  videos,
}: UseOfferBuilderMediaParams): OfferBuilderMediaController {
  const { enqueueSnackbar } = useSnackbar();
  const [uploadedImages, setUploadedImages] = useState<Array<MediaImage>>([]);
  const [uploadedRoomAssignments, setUploadedRoomAssignments] = useState<Array<UploadedRoomAssignment>>([]);
  const [deletedImagePublicIds, setDeletedImagePublicIds] = useState<Array<string>>([]);
  const [hiddenImagePublicIds, setHiddenImagePublicIds] = useState<Array<string>>([]);
  const [imagePendingDeletion, setImagePendingDeletion] = useState<MediaImage | undefined>();
  const [roomImagePendingEdit, setRoomImagePendingEdit] = useState<MediaImage | undefined>();
  const [imageEdits, setImageEdits] = useState<Record<string, LocalImageEdit>>({});
  const [imageRoomOverrides, setImageRoomOverrides] = useState<Record<string, RoomSelection>>({});
  const [localVideoId, setLocalVideoId] = useState<string | undefined>();
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const videoId = localVideoId === '' ? undefined : (localVideoId ?? getPrimaryVideoId(videos));

  const mediaImages = useMemo(() => {
    const deletedImagePublicIdSet = new Set(deletedImagePublicIds);

    return dedupeMediaImages(
      buildMediaImages({
        offerImages: images,
        propertyImages,
        propertyRoomTypes,
      }).concat(uploadedImages),
    )
      .filter((image) => !deletedImagePublicIdSet.has(image.publicId))
      .map((image) => {
        const imageEdit = imageEdits[image.publicId];
        const imageRoomOverride = imageRoomOverrides[image.publicId];

        return {
          ...image,
          sourceLabel: imageRoomOverride?.roomName ?? imageEdit?.roomName ?? image.sourceLabel,
          sourceRoomId: imageRoomOverride ? imageRoomOverride.roomId : (imageEdit?.roomId ?? image.sourceRoomId),
          title: imageEdit?.title ?? image.title,
        };
      });
  }, [
    deletedImagePublicIds,
    imageEdits,
    imageRoomOverrides,
    images,
    propertyImages,
    propertyRoomTypes,
    uploadedImages,
  ]);

  const roomOptions = useMemo<Array<RoomOption>>(
    () =>
      propertyRoomTypes?.map((room, index) => ({
        roomId: getRoomKey(room, index),
        roomName: room.name || DEFAULT_SOURCE_ROOM_NAME,
      })) ?? [],
    [propertyRoomTypes],
  );

  const uploadFiles = async (files: Array<File>, options: { ignoreNonImageFiles?: boolean } = {}) => {
    const candidateFiles = options.ignoreNonImageFiles ? files.filter(isAllowedImageFile) : files;
    const uploadableFiles = candidateFiles.filter(
      (file) => isAllowedImageFile(file) && file.size <= MAX_SVC_IMAGE_UPLOAD_BYTES,
    );
    const rejectedFileCount = candidateFiles.length - uploadableFiles.length;

    if (rejectedFileCount > 0) {
      enqueueSnackbar(
        `${rejectedFileCount} ${rejectedFileCount === 1 ? 'file was' : 'files were'} skipped. Use supported images under 5MB.`,
        { variant: 'warning' },
      );
    }

    if (uploadableFiles.length === 0) {
      return;
    }

    setIsUploadingImages(true);

    try {
      const uploadPlans = buildUploadPlans({
        files: uploadableFiles,
        propertyRoomTypes,
      });

      const uploadResults = await allSettledWithConcurrency(
        uploadPlans,
        IMAGE_UPLOAD_CONCURRENCY,
        async ({ file, roomAssignment }): Promise<{ image: MediaImage; roomAssignment?: UploadedRoomAssignment }> => {
          const response = await uploadImage(file);
          const uploadedImageId = getUploadedImageId(response);

          if (!uploadedImageId) {
            throw new Error('Image service did not return an image id');
          }

          return {
            image: {
              publicId: uploadedImageId,
              sourceLabel: roomAssignment?.roomName ?? UNASSIGNED_IMAGE_LABEL,
              sourceRoomId: roomAssignment?.roomId,
            },
            roomAssignment: roomAssignment
              ? {
                  ...roomAssignment,
                  publicId: uploadedImageId,
                }
              : undefined,
          };
        },
      );
      const uploadedMediaImages: Array<MediaImage> = [];
      const newUploadedRoomAssignments: Array<UploadedRoomAssignment> = [];

      uploadResults.forEach((result) => {
        if (result.status === 'fulfilled') {
          uploadedMediaImages.push(result.value.image);
          if (result.value.roomAssignment) {
            newUploadedRoomAssignments.push(result.value.roomAssignment);
          }
          return;
        }

        if (result.reason instanceof Error) {
          reportError(result.reason);
        }
      });

      const failedUploadCount = uploadResults.length - uploadedMediaImages.length;

      if (uploadedMediaImages.length > 0) {
        setUploadedImages((currentImages) => dedupeMediaImages(currentImages.concat(uploadedMediaImages)));
        setUploadedRoomAssignments((currentAssignments) => currentAssignments.concat(newUploadedRoomAssignments));
        enqueueSnackbar(
          `${uploadedMediaImages.length} ${uploadedMediaImages.length === 1 ? 'image' : 'images'} uploaded`,
          { variant: 'success' },
        );
      }

      if (failedUploadCount > 0) {
        enqueueSnackbar(`${failedUploadCount} ${failedUploadCount === 1 ? 'image' : 'images'} failed to upload`, {
          variant: 'error',
        });
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'Failed to upload images'), { variant: 'error' });
      if (error instanceof Error) {
        reportError(error);
      }
    } finally {
      setIsUploadingImages(false);
    }
  };

  const assignImagesToRoom = (roomId: string, roomName: string, selectedImages: Array<MediaImage>) => {
    setImageRoomOverrides((currentRooms) => {
      const nextRooms = { ...currentRooms };

      selectedImages.forEach((image) => {
        nextRooms[image.publicId] = { roomId, roomName };
      });

      return nextRooms;
    });
    setImageEdits((currentEdits) => {
      const nextEdits = { ...currentEdits };

      selectedImages.forEach((image) => {
        if (nextEdits[image.publicId]) {
          nextEdits[image.publicId] = {
            ...nextEdits[image.publicId],
            roomId,
            roomName,
          };
        }
      });

      return nextEdits;
    });
  };

  const removeImageFromRoom = (publicId: string) => {
    setImageRoomOverrides((currentRooms) => ({
      ...currentRooms,
      [publicId]: UNASSIGNED_ROOM_OPTION,
    }));
    setImageEdits((currentEdits) =>
      currentEdits[publicId]
        ? {
            ...currentEdits,
            [publicId]: {
              ...currentEdits[publicId],
              roomId: undefined,
              roomName: DEFAULT_ROOM_NAME,
            },
          }
        : currentEdits,
    );
  };

  const deleteImage = (publicId: string) => {
    setDeletedImagePublicIds((currentPublicIds) =>
      currentPublicIds.includes(publicId) ? currentPublicIds : currentPublicIds.concat(publicId),
    );
    setHiddenImagePublicIds((currentPublicIds) =>
      currentPublicIds.filter((currentPublicId) => currentPublicId !== publicId),
    );
    setImageRoomOverrides((currentRooms) => {
      const nextRooms = { ...currentRooms };

      delete nextRooms[publicId];

      return nextRooms;
    });
    setImageEdits((currentEdits) => {
      const nextEdits = { ...currentEdits };

      delete nextEdits[publicId];

      return nextEdits;
    });
  };

  const toggleHiddenImage = (publicId: string) => {
    setHiddenImagePublicIds((currentPublicIds) => togglePublicId(currentPublicIds, publicId));
  };

  const editRoomImage = (publicId: string) => {
    const image = mediaImages.find((mediaImage) => mediaImage.publicId === publicId);

    if (image) {
      setRoomImagePendingEdit(image);
    }
  };

  const deleteRoomImageFromEditor = (image: MediaImage) => {
    setImagePendingDeletion(image);
  };

  const saveImageEdit = (publicId: string, edit: LocalImageEdit) => {
    setImageEdits((currentEdits) => ({
      ...currentEdits,
      [publicId]: edit,
    }));
    setImageRoomOverrides((currentRooms) => ({
      ...currentRooms,
      [publicId]: edit.roomId ? { roomId: edit.roomId, roomName: edit.roomName } : UNASSIGNED_ROOM_OPTION,
    }));
  };

  const updateImageTitle = (publicId: string, title: string) => {
    const image = mediaImages.find((mediaImage) => mediaImage.publicId === publicId);

    if (!image) {
      return;
    }

    setImageEdits((currentEdits) => ({
      ...currentEdits,
      [publicId]: {
        ...(currentEdits[publicId] ?? buildDefaultEdit(image, roomOptions)),
        title,
      },
    }));
  };

  const closeDeleteDialog = () => {
    setImagePendingDeletion(undefined);
  };

  const confirmDeleteImage = () => {
    if (!imagePendingDeletion) {
      return;
    }

    deleteImage(imagePendingDeletion.publicId);
    setRoomImagePendingEdit((currentImage) =>
      currentImage?.publicId === imagePendingDeletion.publicId ? undefined : currentImage,
    );
    setImagePendingDeletion(undefined);
  };

  return {
    deletedImagePublicIds,
    hiddenImagePublicIds,
    imageEdits,
    imagePendingDeletion,
    isUploadingImages,
    mediaImages,
    roomImagePendingEdit,
    roomOptions,
    uploadedRoomAssignments,
    videoId,
    closeDeleteDialog,
    confirmDeleteImage,
    deleteImage,
    deleteRoomImageFromEditor,
    removeImageFromRoom,
    saveImageEdit,
    setRoomImagePendingEdit,
    setVideoId: setLocalVideoId,
    toggleHiddenImage,
    updateImageTitle,
    uploadFiles,
    assignImagesToRoom,
    editRoomImage,
  };
}
